import { Injectable, computed, inject } from '@angular/core';

import { CURRENT_USER_ID } from '@common/data';

import { ProfileService } from './api/profile.service';
import { AuthService } from './auth.service';

/**
 * The one answer to "who is signed in". Every feature that needs the current person - ticket
 * assignment, private views, personal milestones, notification preferences - asks here, rather than
 * each holding its own constant.
 */
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private readonly auth = inject(AuthService);
  private readonly profiles = inject(ProfileService);

  /**
   * Directory id, used to key the user's rows in domain data. The profile API does not return a
   * directory id yet, so the seeds' signed-in identity stands in until it does; this is the single
   * place to swap when it arrives.
   */
  readonly id = computed<string>(() => CURRENT_USER_ID);

  /** Cognito subject, or `null` when signed out. */
  readonly sub = computed<string | null>(() => this.auth.userData()?.sub ?? null);

  readonly profile = this.profiles.profile;

  readonly firstName = computed<string>(() => this.profile()?.firstName?.trim() ?? '');
  readonly lastName = computed<string>(() => this.profile()?.lastName?.trim() ?? '');
  readonly jobTitle = computed<string>(() => this.profile()?.jobTitle?.trim() ?? '');
  readonly avatarUrl = computed<string>(() => this.profile()?.avatarUrl ?? '');

  readonly email = computed<string>(
    () => this.auth.userData()?.email ?? this.profile()?.email ?? '',
  );

  readonly displayName = this.auth.displayName;

  readonly role = computed<string>(() => this.auth.userData()?.role ?? '');

  /**
   * Users who signed in through an identity provider have no Cognito password and may have MFA
   * enforced upstream. Cognito marks them with an `identities` claim on the id token.
   */
  readonly isFederated = computed<boolean>(() => {
    const identities = this.auth.userData()?.['identities'];
    return Array.isArray(identities) ? identities.length > 0 : !!identities;
  });

  readonly federatedProvider = computed<string>(() => {
    const identities = this.auth.userData()?.['identities'];
    const first = Array.isArray(identities) ? identities[0] : null;
    const name =
      first && typeof first === 'object'
        ? (first as Record<string, unknown>)['providerName']
        : null;
    return typeof name === 'string' ? name : '';
  });
}
